// @copyright  2006-2022 West Indies Software Engineering Company Limited. All rights reserved.
import store from '@/bootstrap/vuex';
import i18n from '@/bootstrap/i18n';
import { memoDataBelongsTo } from '@/utilities/sales-credit-memo-source';

/**
 * The credit-memo create page reads `action` / `notes` / `issue_credit` from
 * the `selectedInvoice` module, which only the invoice page's Refund dialogue
 * populates (`selectedInvoice/setMemoData`) — and populates for ONE invoice,
 * whose id the mutation records. A deep link on a fresh load finds the module
 * empty; a deep link to another invoice finds a trio that is not its own. Both
 * are turned back to the invoice page before either component mounts (an
 * in-component guard lost the race to the parent list page's own navigation).
 *
 * A params-only push (A's create page → B's) re-runs neither this guard nor
 * `mounted()`; the create page's `beforeRouteUpdate` covers that leg with the
 * same predicate, shared through `utilities/sales-credit-memo-source.js`.
 */

export default [
    {
        path: '/:organisation/receivables/invoices/quick-invoice',
        component: () =>
            import('@/pages/[organisation]/receivables/invoices/quick-invoice.vue'),
        name: 'accounts-receivable.invoices.quick-invoice',
        meta: {
            loginRequired: true,
            permission: 'access.receivables.invoice.create-draft-invoice',
        },
    },
    {
        path: '/:organisation/receivables/invoices',
        component: () =>
            import('@/pages/[organisation]/receivables/invoices.vue'),
        name: 'accounts-receivable.invoices.search-invoices',
        meta: {
            loginRequired: true,
            permission: 'access.receivables.invoice.view-invoices',
        },

        children: [
            {
                path: 'create-invoice',
                component: () =>
                    import('@/pages/[organisation]/receivables/invoices/create-invoice.vue'),
                name: 'accounts-receivable.invoices.create-invoice',
                meta: {
                    loginRequired: true,
                    permission: 'access.receivables.invoice.create-draft-invoice',
                },
            },
            {
                path: ':id/draft',
                component: () =>
                    import(/* webpackChunkName: "accounts-receivable.invoices" */ '@/pages/[organisation]/receivables/invoices/draft-invoice/[id]/edit.vue'),
                name: 'accounts-receivable.invoices.update-draft-invoice',
                meta: {
                    loginRequired: true,
                    permission: 'access.receivables.invoice.update-invoice',
                },
            },
            {
                path: ':id/edit',
                component: () =>
                    import(/* webpackChunkName: "accounts-receivable.invoices" */ '@/pages/[organisation]/receivables/invoices/invoice/[id]/edit.vue'),
                name: 'accounts-receivable.invoices.update-invoice',
                meta: {
                    loginRequired: true,
                    permission: 'access.receivables.invoice.update-invoice',
                },
            },
            {
                path: ':id/duplicate',
                component: () =>
                    import(/* webpackChunkName: "accounts-receivable.invoices" */ '@/pages/[organisation]/receivables/invoices/duplicate-invoice.vue'),
                name: 'accounts-receivable.invoices.duplicate-invoice',
                meta: {
                    loginRequired: true,
                    permission: 'access.receivables.invoice.update-invoice',
                },
            },
            {
                path: ':id',
                component: () =>
                    import(/* webpackChunkName: "accounts-receivable.invoices" */ '@/pages/[organisation]/receivables/invoices/invoice/[id].vue'),
                name: 'accounts-receivable.invoices.view-invoice',
                meta: {
                    loginRequired: true,
                    permission: 'access.receivables.invoice.view-invoices',
                },
                children: [
                    {
                        path: 'email-invoice',
                        component: () =>
                            import(/* webpackChunkName: "accounts-receivable.invoices" */ '@/pages/[organisation]/receivables/invoices/email-invoice.vue'),
                        name: 'accounts-receivable.invoices.email-invoice',
                        meta: {
                            loginRequired: true,
                            permission: 'access.receivables.invoice.update-invoice',
                        },
                    },
                    {
                        path: 'make-payment',
                        component: () =>
                            import(/* webpackChunkName: "accounts-receivable.invoice-payments" */ '@/pages/[organisation]/receivables/invoice-payments/invoice-payment/new.vue'),
                        name: 'accounts-receivable.invoice-payments.create-invoice-payment',
                        meta: {
                            loginRequired: true,
                            permission: 'access.receivables.invoice.create-invoice-payment',
                        },
                    },
                ],
            },
            {
                path: ':id/invoice-payments',
                component: () =>
                    import(/* webpackChunkName: "accounts-receivable.invoice-payments" */ '@/pages/[organisation]/receivables/invoice-payments/invoice-payments.vue'),
                name: 'accounts-receivable.invoice-payments.search-invoice-payments',
                meta: {
                    loginRequired: true,
                    permission: 'access.receivables.invoice.view-invoice-payments',
                },
                children: [
                    {
                        path: ':paymentId/redo-payment',
                        component: () =>
                            import(/* webpackChunkName: "accounts-receivable.invoice-payments" */ '@/pages/[organisation]/receivables/invoice-payments/redo-invoice-payment.vue'),
                        name: 'accounts-receivable.invoice-payments.redo-invoice-payment',
                        meta: {
                            loginRequired: true,
                            permission: 'access.receivables.invoice.create-invoice-payment',
                        },
                    },
                ],
            },
            {
                path: ':id/history',
                component: () =>
                    import(/* webpackChunkName: "accounts-receivable.invoices" */ '@/pages/[organisation]/receivables/invoices/invoice-history.vue'),
                name: 'accounts-receivable.invoices.view-invoice-history',
                meta: {
                    loginRequired: true,
                    permission: 'access.receivables.invoice.view-invoices',
                },
            },
            {
                path: ':id/create-credit-memo',
                component: () =>
                    import(/* webpackChunkName: "accounts-receivable.sales-credit-memos" */ '@/pages/[organisation]/receivables/sales-credit-memos/sales-credit-memo/new.vue'),
                name: 'accounts-receivable.sales-credit-memos.create-sales-credit-memo',
                meta: {
                    loginRequired: true,
                    permission: 'access.receivables.sales-credit-memo.create-sales-credit-memo',
                },
                beforeEnter: (to, from, next) => {
                    if (!memoDataBelongsTo(store.state.selectedInvoice, to.params.id)) {
                        window.flashMessage({
                            type: 'info',
                            message: i18n.global.t('sales-credit-memo.info.message.start_from_invoice'),
                        });

                        return next({ name: 'accounts-receivable.invoices.view-invoice', params: to.params });
                    }

                    next();
                },
            },
        ],
    },
];
